import { errorLogger } from "@/Common/helpers/logger";
import { useSnackbar } from "@/Common/hooks/Snackbar/useSnackbar";
import { recalculateWashout } from "@/Washouts/api/common/mutations";
import { createChangeRequest } from "@/Washouts/api/WashoutReview/mutations";
import { createOrUpdateChangeRequestCallback } from "@/Washouts/helpers/ChangeRequest/subscriptionCallbacks";
import { IRecalculateWashoutsParams } from "@/Washouts/types/Api/commons";
import { IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { IWashoutQueries } from "@/Washouts/types/WashoutReview";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface IWashoutData {
	washoutQueries: IWashoutQueries;
	position: string;
	positionType?: string;
}

interface IWashoutActionParams extends IWashoutData {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	userId: string;
	onRecalculateWashout?: () => void;
}

export const useWashoutReviewActions = (props: IWashoutActionParams) => {
	const { washoutQueries, position, payPeriodId, userId, locationId, commissionCycleId, onRecalculateWashout } = props;
	const queryClient = useQueryClient();
	const { showSnackBar } = useSnackbar();

	const { mutate: createChangeRequestMutation, isPending: isPendingCreateChangeRequest } = useMutation({
		mutationKey: ["createChangeRequest", locationId, commissionCycleId, payPeriodId, origin, userId, position],
		mutationFn: (input: { description?: string; issueSummary: string; origin: string; position: string }) =>
			createChangeRequest({ locationId, payPeriodId, userId, input, commissionCycleId }),
		onSuccess: ({ data }: { data: { createChangeRequest: IGetChangeRequestsByCommissionCycle } }) => {
			createOrUpdateChangeRequestCallback(data.createChangeRequest, queryClient, true);
		},
	});

	const { mutate: updateWashoutStatusMutation, isPending: isPendingUpdateWashout } = useMutation({
		mutationKey: ["updateWashoutStatus", locationId, commissionCycleId, payPeriodId, userId],
		mutationFn: (input: { status: { status: string; statusSetBy: string | undefined; statusSetOn: Date } }) => {
			return washoutQueries.updateWashoutStatus(locationId, commissionCycleId, payPeriodId, userId, input.status, position);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(["getWashout", payPeriodId, position, userId], (oldData: IWashoutData) => {
				return { ...oldData, review: data?.review };
			});
		},
	});

	const { mutate: generateWashoutMutation, isPending: isPendingGenerateWashout } = useMutation({
		mutationKey: ["generateWashout", locationId, commissionCycleId, payPeriodId, userId],
		mutationFn: () => washoutQueries.generateWashout(locationId, commissionCycleId, payPeriodId, [userId]),
		onSuccess: () => {
			showSnackBar("Washout generated successfully");
		},
		onError: (err) => {
			showSnackBar(`Error generating washout: ${JSON.stringify(err)}`, true);
			errorLogger(err);
		},
	});

	const { mutate: recalculateWashoutMutation, isPending: isPendingWashoutRecalculation } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params: IRecalculateWashoutsParams) => {
			onRecalculateWashout?.();
			return recalculateWashout(params);
		},
	});

	return {
		createChangeRequestMutation,
		isPendingCreateChangeRequest,
		updateWashoutStatusMutation,
		isPendingUpdateWashout,
		generateWashoutMutation,
		isPendingGenerateWashout,
		recalculateWashoutMutation,
		isPendingWashoutRecalculation,
	};
};
